import React, { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import { UnauthorizedHeader } from '@app/layouts/UnauthorizedPageLayout/header/styles';
import { getRefreshToken } from '@app/redux/auth/auth.selectors';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { mfaActions } from '@app/redux/mfa/mfa.reducer';
import RoutePaths from '@app/routes/paths';
import useGenerateRoutePath from '@app/routes/paths/useGenerateRoutePath';

import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import ApplicationLogo from '@components/logo';

import * as environment from '@utils/environment';
import { isAllowUserRegistration } from '@utils/environment';

function UnauthorizedPageHeader({ isPostLogin }: { isPostLogin?: boolean }) {
	const dispatch = useAppDispatch();
	const history = useHistory();
	const location = useLocation();
	const generateRoutePath = useGenerateRoutePath();
	const refreshToken = useAppSelector(getRefreshToken);
	const { startRequest: logout, isPending: isLoggingOut } = useTriggeredRequest(AccountClientRequests.Auth.logout);

	const isLoginPage = location.pathname.includes('login');

	const handleClick = () => {
		if (isLoginPage) {
			if (isAllowUserRegistration()) {
				history.push(generateRoutePath(RoutePaths.AUTH.REGISTRATION));
			}
		} else {
			history.push(generateRoutePath(RoutePaths.AUTH.LOGIN));
		}
	};

	const handleLogout = () => {
		if (refreshToken) logout({ refresh_token: refreshToken });
		else {
			dispatch(mfaActions.clearMFAPreconditionData());
			history.push(generateRoutePath(RoutePaths.AUTH.LOGIN));
		}
	};

	const buttonConfig = useMemo(() => {
		return {
			isVisible:
				!isLoginPage ||
				isAllowUserRegistration() ||
				(!isAllowUserRegistration() && environment.env.FIRM_ENVIRONMENT === 'invertir-online'),
			label: isLoginPage ? "Don't have an account?" : 'Already have an account?',
			buttonText: isLoginPage ? 'Sign up' : 'Log in',
			handleClick: handleClick,
		};
	}, [generateRoutePath, history, isLoginPage]);

	const renderActionButton = () => {
		if (isPostLogin) {
			return (
				<Button onClick={handleLogout} sx={{ textTransform: 'initial' }} variant="contained" color="primary">
					<b>Log out</b>
					{isLoggingOut && (
						<Box ml={1} sx={{ display: 'flex', alignItems: 'center' }}>
							<CircularProgress size={16} color={'secondary'} />
						</Box>
					)}
				</Button>
			);
		} else if (buttonConfig.isVisible) {
			return (
				<Stack direction={'row'} alignItems={'center'}>
					<Typography noWrap variant="caption" pr={3}>
						<b>{buttonConfig.label}</b>
					</Typography>

					<Button
						variant="contained"
						color="primary"
						onClick={buttonConfig.handleClick}
						sx={{ textTransform: 'initial' }}
					>
						<b>{buttonConfig.buttonText}</b>
					</Button>
				</Stack>
			);
		} else return null;
	};

	return (
		<UnauthorizedHeader>
			<ApplicationLogo />
			{renderActionButton()}
		</UnauthorizedHeader>
	);
}

export default UnauthorizedPageHeader;
